import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, User, ChevronDown, LogOut } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { ConnexionDialog } from "@/components/ConnexionDialog";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Liens principaux visibles dans la barre (parcours d'achat)
const mainLinks = [
  { to: "/", label: "Accueil" },
  { to: "/boutique", label: "Boutique" },
  { to: "/realisations", label: "Réalisations" },
];

// Liens regroupés sous le menu déroulant « Découvrir »
const discoverLinks = [
  { to: "/gardiens", label: "Les Gardiens" },
  { to: "/nos-techniques", label: "Techniques" },
  { to: "/pathologies", label: "Pathologies" },
  { to: "/temoignages", label: "Témoignages" },
  { to: "/espace-jeux", label: "Espace Jeux" },
  { to: "/professionnels", label: "Professionnels" },
  { to: "/forum", label: "Forum" },
];

const endLinks = [
  { to: "/personnalisation", label: "Personnaliser" },
  { to: "/contact", label: "Sur Mesure" },
];

// Liste complète pour le menu mobile (à plat)
const links = [...mainLinks, ...discoverLinks, ...endLinks];

export const Navbar = () => {
  const { cartCount, setCartOpen, connexionOpen, setConnexionOpen, portalUser, refreshPortal } = useApp();

  const handleLogout = async () => {
    try { await api.post("/portal/logout"); } catch {}
    refreshPortal();
    toast.success("Vous êtes déconnecté(e)");
  };
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => setOpen(false), [loc.pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${scrolled ? "bg-background/90 backdrop-blur-xl border-b border-border shadow-sm" : "bg-background/70 backdrop-blur-md"}`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-8 h-12 lg:h-14 flex items-center justify-between gap-4">
        <Link to="/" data-testid="logo-link" className="flex items-center shrink-0">
          <img src="/logo-pont.png?v=6" alt="Passerelle — objets personnalisés pour l'accompagnement" className="h-8 lg:h-10 w-auto object-contain" />
        </Link>

        <div className="hidden lg:flex items-center gap-4 xl:gap-5">
          {mainLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]/g, "")}`}
              className={`text-[13px] xl:text-sm font-semibold whitespace-nowrap transition-colors hover:text-primary ${loc.pathname === l.to ? "text-primary" : "text-foreground/80"}`}
            >
              {l.label}
            </Link>
          ))}

          {/* Menu déroulant « Découvrir » */}
          <div className="relative group">
            <button
              data-testid="nav-decouvrir"
              className={`flex items-center gap-1 text-[13px] xl:text-sm font-semibold whitespace-nowrap transition-colors hover:text-primary ${discoverLinks.some((l) => l.to === loc.pathname) ? "text-primary" : "text-foreground/80"}`}
            >
              Découvrir <ChevronDown className="w-3.5 h-3.5 transition-transform duration-300 group-hover:rotate-180" />
            </button>
            <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 transition-all duration-200">
              <div className="bg-background border border-border/60 rounded-2xl shadow-[0_16px_40px_rgb(0,0,0,0.10)] py-2 min-w-[190px]">
                {discoverLinks.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    data-testid={`nav-decouvrir-${l.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                    className={`block px-5 py-2.5 text-sm font-medium whitespace-nowrap transition-colors hover:bg-accent hover:text-primary ${loc.pathname === l.to ? "text-primary" : "text-foreground/80"}`}
                  >
                    {l.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {endLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]/g, "")}`}
              className={`text-[13px] xl:text-sm font-semibold whitespace-nowrap transition-colors hover:text-primary ${loc.pathname === l.to ? "text-primary" : "text-foreground/80"}`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-3">
          {portalUser ? (
            <div className="relative group hidden sm:block">
              <button data-testid="open-profile-menu" className="flex items-center gap-2 px-2 py-1 text-sm text-foreground/80 hover:text-primary transition-colors">
                <span className="w-8 h-8 rounded-full bg-primary/15 text-primary flex items-center justify-center font-semibold text-xs">
                  {(portalUser.name || portalUser.email || "?").slice(0, 2).toUpperCase()}
                </span>
                <span className="max-w-[110px] truncate">{portalUser.name || "Mon compte"}</span>
              </button>
              <div className="absolute right-0 top-full pt-2 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 transition-all duration-200">
                <div className="bg-background border border-border/60 rounded-2xl shadow-[0_16px_40px_rgb(0,0,0,0.10)] py-2 min-w-[200px]">
                  <p className="px-5 py-2 text-xs text-muted-foreground truncate border-b border-border/40 mb-1">{portalUser.email}</p>
                  <Link to="/mon-compte" data-testid="nav-profile-link" className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium hover:bg-accent hover:text-primary transition-colors">
                    <User className="w-4 h-4" /> Mon profil
                  </Link>
                  <button onClick={handleLogout} data-testid="nav-logout-btn" className="w-full flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-foreground/80 hover:bg-accent hover:text-primary transition-colors">
                    <LogOut className="w-4 h-4" /> Déconnexion
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <button
              data-testid="open-connexion-btn"
              onClick={() => setConnexionOpen(true)}
              className="hidden sm:flex items-center gap-1.5 text-sm text-foreground/70 hover:text-primary transition-colors px-2 py-1"
            >
              <User className="w-4 h-4" strokeWidth={1.6} /> Connexion
            </button>
          )}
          <button
            data-testid="open-cart-btn"
            onClick={() => setCartOpen(true)}
            className="relative p-2 rounded-full hover:bg-accent transition-colors"
            aria-label="Panier"
          >
            <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
            {cartCount > 0 && (
              <span data-testid="cart-count" className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full bg-primary text-primary-foreground text-[11px] flex items-center justify-center">
                {cartCount}
              </span>
            )}
          </button>
          <button className="lg:hidden p-2" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="mobile-menu-btn">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      <ConnexionDialog open={connexionOpen} onOpenChange={setConnexionOpen} />

      {open && (
        <div className="lg:hidden bg-background/95 backdrop-blur-xl border-b border-border px-6 py-6 flex flex-col gap-4">
          {links.map((l) => (
            <Link key={l.to} to={l.to} className="text-lg text-foreground/80" data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]/g, "")}`}>
              {l.label}
            </Link>
          ))}
          {portalUser ? (
            <>
              <Link to="/mon-compte" onClick={() => setOpen(false)} className="text-lg text-primary flex items-center gap-2" data-testid="mobile-profile-link">
                <User className="w-5 h-5" /> Mon profil
              </Link>
              <button onClick={() => { handleLogout(); setOpen(false); }} className="text-lg text-foreground/80 text-left flex items-center gap-2" data-testid="mobile-logout-btn">
                <LogOut className="w-5 h-5" /> Déconnexion
              </button>
            </>
          ) : (
            <button onClick={() => { setOpen(false); setConnexionOpen(true); }} className="text-lg text-primary text-left flex items-center gap-2" data-testid="mobile-connexion-btn">
              <User className="w-5 h-5" /> Connexion
            </button>
          )}
        </div>
      )}
    </header>
  );
};
